import { useState, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { SyncAltOutlined } from "@mui/icons-material";
import { fmt } from "../types";
import type { InputMode } from "../types";
import { SecuredIndicator } from "./SecuredIndicator";

const compactFieldSx = {
  "& .MuiInputBase-root": { height: 28 },
  "& .MuiInputBase-input": {
    py: 0.25,
    fontSize: "0.8rem",
    textAlign: "right",
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    MozAppearance: "textfield",
  },
} as const;

interface TotalEditCellProps {
  currentTotal: number;
  securedPct: number | null;
  onDistribute: (total: number) => void;
  onDistributePercent?: (percent: number) => void;
  label?: string;
}

export function TotalEditCell({
  currentTotal,
  securedPct,
  onDistribute,
  onDistributePercent,
  label,
}: TotalEditCellProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [mode, setMode] = useState<InputMode>("number");
  const inputRef = useRef<HTMLInputElement>(null);

  const isPctMode = mode === "percent" && !!onDistributePercent;

  const handleBlur = () => {
    const val = Number(editValue) || 0;
    const hasInput = editValue.trim() !== "";
    if (hasInput && (!isPctMode ? val !== currentTotal : true)) {
      setShowConfirm(true);
    } else {
      setEditValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === "Escape") {
      setEditValue("");
      inputRef.current?.blur();
    }
  };

  const handleConfirm = () => {
    const val = Number(editValue) || 0;
    if (isPctMode) {
      onDistributePercent!(val);
    } else {
      onDistribute(val);
    }
    setEditValue("");
    setShowConfirm(false);
  };

  const handleCancel = () => {
    setEditValue("");
    setShowConfirm(false);
  };

  const toggleMode = () => {
    setMode((m) => (m === "percent" ? "number" : "percent"));
    setEditValue("");
  };

  const confirmText = isPctMode
    ? `Detta sätter alla anläggningar till ${Number(editValue) || 0}% av prognos för ${label ?? "alla kvartal"}.`
    : label
      ? `Detta skriver om ${label} volymer för alla anläggningar med den nya totalen (${fmt.format(Number(editValue) || 0)} kWh), fördelat efter prognosens fördelning.`
      : `Detta skriver om alla kvartals säkrade volymer med den nya totalen (${fmt.format(Number(editValue) || 0)} kWh), fördelat efter prognosens kvartalsfördelning.`;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          justifyContent: "flex-end",
        }}
      >
        <TextField
          inputRef={inputRef}
          size="small"
          type="number"
          placeholder={isPctMode ? "0" : fmt.format(currentTotal)}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          sx={onDistributePercent
            ? { width: isPctMode ? 84 : 110, ...compactFieldSx }
            : { width: 120, ...compactFieldSx }
          }
          slotProps={onDistributePercent
            ? {
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.65rem", lineHeight: 1 }}
                      >
                        {isPctMode ? "%" : "kWh"}
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }
            : {
                htmlInput: {
                  style: { textAlign: "right", fontWeight: 600 },
                },
              }
          }
        />
        {onDistributePercent && (
          <Tooltip title={isPctMode ? "Växla till kWh" : "Växla till %"}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); toggleMode(); }}
              sx={{ p: 0.25 }}
            >
              <SyncAltOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <SecuredIndicator percentage={securedPct} />

      <Dialog open={showConfirm} onClose={handleCancel} maxWidth="xs">
        <DialogTitle>
          {isPctMode
            ? `Sätt procentandel av prognos${label ? ` för ${label}` : ""}?`
            : label
              ? `Skriv om ${label}-volymer?`
              : "Skriv om kvartalsvolymer?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Avbryt</Button>
          <Button onClick={handleConfirm} variant="contained">
            Fortsätt
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
